import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  breadcrumbs: BreadcrumbItem[];
  title?: string;
  description?: string;
  className?: string;
  fallbackTo?: string;
}

/**
 * Standard internal-page header: Voltar button + breadcrumb + title + description.
 * Used across converters, extractors, dashboard tools, etc. for consistent UX.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  breadcrumbs,
  title,
  description,
  className,
  fallbackTo = '/app',
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1 && document.referrer) {
      navigate(-1);
    } else {
      navigate(fallbackTo);
    }
  };

  const items: BreadcrumbItem[] = [{ label: 'Início', to: '/' }, ...breadcrumbs];

  return (
    <div className={cn('w-full max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-2', className)}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20 transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Voltar
        </button>

        <nav aria-label="breadcrumb" className="flex items-center flex-wrap gap-1 text-xs text-slate-400">
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            return (
              <React.Fragment key={`${item.label}-${idx}`}>
                {idx > 0 && <ChevronRight className="w-3 h-3 text-slate-600 mx-0.5" />}
                {item.to && !isLast ? (
                  <Link
                    to={item.to}
                    className="hover:text-cyan-300 transition inline-flex items-center gap-1"
                  >
                    {idx === 0 && <Home className="w-3 h-3" />}
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      isLast ? 'text-slate-200 font-semibold' : ''
                    )}
                  >
                    {idx === 0 && <Home className="w-3 h-3" />}
                    {item.label}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {(title || description) && (
        <div className="mb-4">
          {title && (
            <h1 className="text-2xl sm:text-3xl font-black text-white uppercase italic tracking-tighter">
              {title}
            </h1>
          )}
          {description && (
            <p className="mt-1 text-sm text-slate-400 max-w-3xl">{description}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
